import { ClickAwayListener, Paper, Popper, PopperPlacementType } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import React, { FC, ReactNode, useMemo } from 'react';

import { EASE_STANDARD, MENU_IN_DOWN, MENU_IN_UP } from '../common/createSendMotion';

interface IProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  children: ReactNode;
  matchAnchorWidth?: boolean;
  role?: string;
  paperSx?: SxProps<Theme>;
  placement?: PopperPlacementType;
}

const DropdownPopper: FC<IProps> = ({
  open,
  anchorEl,
  onClose,
  children,
  matchAnchorWidth,
  role,
  paperSx,
  placement = 'bottom-start',
}) => {
  const modifiers = useMemo(
    () => [
      { name: 'flip', options: { fallbackPlacements: [placement.startsWith('top') ? 'bottom-start' : 'top-start'] } },
      { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
      { name: 'offset', options: { offset: [0, 4] } },
    ],
    [placement],
  );

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={modifiers}
      sx={{ zIndex: (theme: Theme) => theme.zIndex.modal + 1 }}
    >
      {({ placement: resolvedPlacement }) => {
        const isTop = resolvedPlacement.startsWith('top');
        return (
          <ClickAwayListener
            onClickAway={(event) => {
              if (anchorEl && anchorEl.contains(event.target as Node)) {
                return;
              }
              onClose();
            }}
          >
            <Paper
              role={role}
              sx={{
                ...(matchAnchorWidth && anchorEl ? { width: anchorEl.clientWidth } : {}),
                maxHeight: 300,
                overflowY: 'auto',
                borderRadius: 1,
                py: 1,
                boxShadow: (theme: Theme) => theme.shadows[8],
                transformOrigin: isTop ? 'center bottom' : 'center top',
                animation: `${isTop ? MENU_IN_UP : MENU_IN_DOWN} 120ms ${EASE_STANDARD}`,
                ...paperSx,
              }}
            >
              {children}
            </Paper>
          </ClickAwayListener>
        );
      }}
    </Popper>
  );
};

export default DropdownPopper;
